import { useState, useEffect, useMemo, useRef } from "react";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { useToast } from "./ui/ToastModal";
import {
    X,
    Save,
    Loader2,
    Lock,
    Unlock,
    History,
    RotateCcw,
    ChevronDown,
    ChevronUp,
    FileText,
    Clock,
    User,
} from "lucide-react";
import { formatCurrency } from "../lib/calculations";
import { GST_RATE, calculateRetentionBeforeGst, roundCurrency } from "../lib/claimFinancials";
import { regenerateClaimSummaryPdf, type RegenReport } from "../lib/regenerateClaimSummaryPdf";
import {
    hasRestorablePaymentSnapshot,
    revertToPaymentVersion,
    type PaymentVersionRow,
} from "../lib/paymentVersions";
import { logError } from "../lib/logError";

// ─── Types ────────────────────────────────────────────────────
interface ClaimReport {
    id: string;
    project_id: string;
    claim_number: number;
    claim_month: number;
    claim_year: number;
    original_contract_sum: number | null;
    claimed_to_date: number | null;
    variations_this_month: number | null;
    variations_outstanding: number | null;
    subtotal: number | null;
    retention_pct: number | null;
    retention_amount: number | null;
    retention_release_amount: number | null;
    retention_release_gst: number | null;
    retention_release_total_inc_gst: number | null;
    claim_this_month: number | null;
    total_exc_gst: number | null;
    total_inc_gst: number | null;
    paid_by_builder: number | null;
    calculated_payment_amount: number | null;
    difference: number | null;
    days_before_due: number | null;
    date_paid: string | null;
    receipt_url: string | null;
    claimed_set?: {
        install_hire_this_month?: number | null;
        hire_billing?: unknown[] | null;
    } | null;
    pdf_url: string | null;
    pdf_filename: string | null;
    submission_date: string | null;
    payment_due_date: string | null;
    notes: string | null;
    is_manual_entry: boolean | null;
    created_at: string;
    created_by: string | null;
}

interface VersionRow {
    id: string;
    report_id: string;
    version_number: number;
    version_type: string;
    pdf_url: string | null;
    snapshot: Record<string, unknown>;
    changes_summary: string;
    changed_by_name: string | null;
    created_at: string;
}

interface Props {
    report: ClaimReport;
    previousReport: ClaimReport | null;
    onClose: () => void;
    onSaved: () => void;
}

const MONTH_SHORT = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const deriveStoredSubtotal = (report: ClaimReport) => {
    const storedSubtotal = Number(report.subtotal || 0);
    if (storedSubtotal > 0) return storedSubtotal;
    return Number(report.claim_this_month || 0) + Number(report.retention_amount || 0);
};

const subtotalExGstBase = (subtotalExGst: number) => roundCurrency(subtotalExGst);

// SEQ-145: new-format reports carry claimed_set.summary (A/B/C/R/T), where
// total_exc_gst = C - R + release no longer tracks claim_this_month. The lock
// heuristics below assume total_exc_gst ~= claim_this_month, so detect the new
// shape and leave the locks off (preserve stored figures, no mount recalc).
// claimed_set may arrive as a JSON string, an object, or null.
const claimedSetHasSummary = (claimedSet: unknown): boolean => {
    if (!claimedSet) return false;
    let parsed: unknown = claimedSet;
    if (typeof claimedSet === "string") {
        try {
            parsed = JSON.parse(claimedSet);
        } catch {
            return false;
        }
    }
    return (
        typeof parsed === "object" &&
        parsed !== null &&
        "summary" in parsed &&
        Boolean((parsed as Record<string, unknown>).summary)
    );
};

// ─── Component ────────────────────────────────────────────────
export function ClaimEditModal({ report, previousReport, onClose, onSaved }: Props) {
    const { addToast } = useToast();
    const [saving, setSaving] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [versions, setVersions] = useState<VersionRow[]>([]);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [revertConfirm, setRevertConfirm] = useState<VersionRow | null>(null);

    // ─── Editable fields ───────────────────────────────────────
    const [claimedToDate, setClaimedToDate] = useState(String(report.claimed_to_date ?? ""));
    const [variationsThisMonth, setVariationsThisMonth] = useState(String(report.variations_this_month ?? ""));
    const [variationsOutstanding, setVariationsOutstanding] = useState(String(report.variations_outstanding ?? ""));
    const [claimThisMonth, setClaimThisMonth] = useState(String(report.claim_this_month ?? ""));

    // Subtotal = this claim before GST and before retention.
    // Older rows may not have subtotal, so derive from the legacy claim + retention storage.
    const [subtotal, setSubtotal] = useState(() => {
        return String(deriveStoredSubtotal(report));
    });

    // Retention %: auto-compute on init if DB has null/0 but retention_amount > 0
    const [retentionPct, setRetentionPctRaw] = useState(() => {
        const pct = report.retention_pct;
        const amt = Number(report.retention_amount || 0);
        // If pct is missing/zero but we have a real retention amount, derive %
        if ((!pct || pct === 0) && amt > 0) {
            const sub = deriveStoredSubtotal(report);
            const exGstBase = subtotalExGstBase(sub);
            if (exGstBase > 0) return ((amt / exGstBase) * 100).toFixed(2);
        }
        return String(pct ?? "");
    });
    const [retentionAmount, setRetentionAmountRaw] = useState(String(report.retention_amount ?? ""));

    // Track which retention field was last manually edited to enable bidirectional sync
    // 'init' = just loaded, don't auto-calc (preserve DB values)
    // 'pct'  = user edited %, auto-calc $
    // 'amt'  = user edited $, auto-calc %
    const retentionSource = useRef<'init' | 'pct' | 'amt'>('init');
    const [totalExcGst, setTotalExcGst] = useState(String(report.total_exc_gst ?? ""));
    const [totalIncGst, setTotalIncGst] = useState(String(report.total_inc_gst ?? ""));
    const [submissionDate, setSubmissionDate] = useState(report.submission_date ?? "");
    const [paymentDueDate, setPaymentDueDate] = useState(report.payment_due_date ?? "");
    const [paidByBuilder, setPaidByBuilder] = useState(String(report.paid_by_builder ?? ""));
    const [datePaid, setDatePaid] = useState(report.date_paid ?? "");
    const [notes, setNotes] = useState(report.notes ?? "");

    // New-format (SEQ-145) reports store a claimed_set.summary; their total_exc_gst
    // is C - R + release, so the equality-based lock heuristics do not apply.
    const hasSummaryClaimedSet = claimedSetHasSummary(report.claimed_set);

    // ─── Override locks (locked = auto-calculated) ──────────────
    // Smart-lock: only auto-calculate if the stored value matches the formula.
    // This prevents overwriting manually-set or imported values on mount.
    //
    // Edge function stores claim_this_month as the ex-GST subtotal before retention.
    // For legacy rows without subtotal, preserve manual values instead of auto-overwriting them.
    const [lockThisClaim, setLockThisClaimRaw] = useState(() => {
        if (hasSummaryClaimedSet) return false; // New format: preserve stored value.
        const storedSubtotal = Number(report.subtotal || 0);
        const sub = deriveStoredSubtotal(report);
        const stored = Number(report.claim_this_month || 0);
        if (sub === 0 && stored === 0) return true; // Both zero → nothing to override
        if (sub === 0) return false; // Has claim but no subtotal derivation possible
        if (storedSubtotal <= 0) return false;
        if (Math.abs(stored - sub) >= 1) return false; // Stored subtotal/claim disagree → preserve
        // The auto-calc formula treats claimed_to_date as cumulative
        // (this month = claimed − previous claimed). Some rows store a
        // non-cumulative claimed_to_date, which would make auto-calc produce a
        // negative/garbage subtotal and corrupt correct stored values on mount.
        // Only auto-calc (lock) when the stored subtotal matches that formula.
        const claimed = Number(report.claimed_to_date || 0);
        const prev = Number(previousReport?.claimed_to_date || 0);
        const vars = Number(report.variations_this_month || 0);
        const varsOut = Number(report.variations_outstanding || 0);
        const formulaSubtotal = claimed - prev + vars + varsOut;
        return Math.abs(storedSubtotal - formulaSubtotal) < 1;
    });
    // Retention fields are always user-controlled, bidirectional sync is handled by retentionSource ref.
    // Edge function stores: total_exc_gst = claim_this_month (they are IDENTICAL)
    const [lockExcGst, setLockExcGstRaw] = useState(() => {
        if (hasSummaryClaimedSet) return false; // New format: total_exc_gst = C - R + release.
        const thisClaim = Number(report.claim_this_month || 0);
        const stored = Number(report.total_exc_gst || 0);
        return Math.abs(stored - thisClaim) < 1; // They should be equal
    });
    const [lockIncGst, setLockIncGstRaw] = useState(() => {
        if (hasSummaryClaimedSet) return false; // New format: preserve stored total_inc_gst (T).
        const subtotalForReport = deriveStoredSubtotal(report);
        const retentionForReport = Number(report.retention_amount || 0);
        const releaseForReport = Number(report.retention_release_amount || 0);
        const stored = Number(report.total_inc_gst || 0);
        const calc = roundCurrency(
            (Math.max(0, subtotalForReport - retentionForReport) + releaseForReport) * (1 + GST_RATE),
        );
        return Math.abs(stored - calc) < 1;
    });

    // Ref to skip auto-recalculation on pure lock toggles.
    // When user clicks lock icon, we just freeze/unfreeze, don't immediately recalculate.
    const lockJustToggled = useRef(false);
    const setLockThisClaim = (val: boolean) => {
        lockJustToggled.current = true;
        setLockThisClaimRaw(val);
    };
    const setLockExcGst = (val: boolean) => {
        lockJustToggled.current = true;
        setLockExcGstRaw(val);
    };
    const setLockIncGst = (val: boolean) => {
        lockJustToggled.current = true;
        setLockIncGstRaw(val);
    };

    // ─── Auto-calculations ─────────────────────────────────────
    // Chain: subtotal -> retention before GST -> invoice ex GST -> final payable
    //
    // subtotal = claimedToDate - prevClaimed + variations (ex GST, before retention)
    // claimThisMonth = subtotal (ex GST, before retention)
    const prevClaimed = Number(previousReport?.claimed_to_date || 0);

    // When inputs change, recompute subtotal, then derive claimThisMonth
    useEffect(() => {
        if (lockThisClaim) {
            // Skip recalc if user just toggled the lock, preserve current values
            if (lockJustToggled.current) {
                lockJustToggled.current = false;
                return;
            }
            const claimed = Number(claimedToDate || 0);
            const vars = Number(variationsThisMonth || 0);
            const varsOut = Number(variationsOutstanding || 0);
            const calcSubtotal = claimed - prevClaimed + vars + varsOut;
            setSubtotal(calcSubtotal.toFixed(2));
            setClaimThisMonth(calcSubtotal.toFixed(2));
        }
    }, [claimedToDate, variationsThisMonth, variationsOutstanding, prevClaimed, lockThisClaim]);

    // ─── Bidirectional retention sync (using ex-GST subtotal as base) ───
    // Edge function: retentionAmt = subtotal * (pct / 100)
    // Inverse:       pct = retentionAmt / subtotal * 100

    // Direction 1: retention % changed → recalculate retention $
    useEffect(() => {
        if (retentionSource.current === 'pct') {
            const sub = Number(subtotal || 0);
            const pct = Number(retentionPct || 0);
            const calc = calculateRetentionBeforeGst({ subtotalExGst: sub, retentionPct: pct });
            setRetentionAmountRaw(calc.retentionAmount >= 0 ? calc.retentionAmount.toFixed(2) : "0");
        }
    }, [retentionPct, subtotal]);

    // Direction 2: retention $ changed → recalculate retention %
    useEffect(() => {
        if (retentionSource.current === 'amt') {
            const sub = Number(subtotal || 0);
            const amt = Number(retentionAmount || 0);
            const exGstBase = subtotalExGstBase(sub);
            if (exGstBase > 0) {
                const pct = (amt / exGstBase) * 100;
                setRetentionPctRaw(pct.toFixed(2));
            } else {
                setRetentionPctRaw("0");
            }
        }
    }, [retentionAmount, subtotal]);

    // When subtotal changes (upstream from claimedToDate/variations), recalculate retention $
    useEffect(() => {
        if (retentionSource.current === 'init') return; // Don't override DB values on mount
        if (retentionSource.current === 'pct') {
            // Pct is the anchor: recalculate $ based on new ex-GST subtotal
            const sub = Number(subtotal || 0);
            const pct = Number(retentionPct || 0);
            const calc = calculateRetentionBeforeGst({ subtotalExGst: sub, retentionPct: pct });
            setRetentionAmountRaw(calc.retentionAmount >= 0 ? calc.retentionAmount.toFixed(2) : "0");
        }
        // If source is 'amt', the $ stays as-is and % recalculates (handled above)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subtotal]);

    // Wrapped setters that track edit source
    const setRetentionPct = (val: string) => {
        retentionSource.current = 'pct';
        setRetentionPctRaw(val);
    };
    const setRetentionAmount = (val: string) => {
        retentionSource.current = 'amt';
        setRetentionAmountRaw(val);
    };

    const setClaimThisMonthManual = (val: string) => {
        setClaimThisMonth(val);
        if (!lockThisClaim) setSubtotal(val);
    };

    // Edge function: total_exc_gst = ex-GST amount after retention held plus release.
    useEffect(() => {
        if (lockExcGst) {
            if (lockJustToggled.current) {
                lockJustToggled.current = false;
                return;
            }
            const thisClaim = Number(claimThisMonth || 0);
            const ret = Number(retentionAmount || 0);
            const release = Number(report.retention_release_amount || 0);
            const invoiceExGst = roundCurrency(Math.max(0, thisClaim - ret) + release);
            setTotalExcGst(invoiceExGst.toFixed(2));
        }
    }, [claimThisMonth, retentionAmount, report.retention_release_amount, lockExcGst]);

    useEffect(() => {
        if (lockIncGst) {
            if (lockJustToggled.current) {
                lockJustToggled.current = false;
                return;
            }
            const excGst = Number(totalExcGst || 0);
            const calc = roundCurrency(excGst * (1 + GST_RATE));
            setTotalIncGst(calc.toFixed(2));
        }
    }, [totalExcGst, lockIncGst]);

    // ─── Changes summary builder ───────────────────────────────
    const buildChangesSummary = (): string => {
        const changes: string[] = [];
        const cmp = (label: string, original: unknown, newVal: string) => {
            const origStr = String(original ?? "");
            if (origStr !== newVal && !(origStr === "" && newVal === "0")) {
                if (!isNaN(Number(origStr)) && !isNaN(Number(newVal))) {
                    changes.push(`${label}: ${formatCurrency(Number(origStr))} → ${formatCurrency(Number(newVal))}`);
                } else {
                    changes.push(`${label}: "${origStr || "—"}" → "${newVal || "—"}"`);
                }
            }
        };
        cmp("Claimed to Date", report.claimed_to_date, claimedToDate);
        cmp("Variations This Month", report.variations_this_month, variationsThisMonth);
        cmp("Variations Outstanding", report.variations_outstanding, variationsOutstanding);
        cmp("This Claim", report.claim_this_month, claimThisMonth);
        cmp("Retention %", report.retention_pct, retentionPct);
        cmp("Retention $", report.retention_amount, retentionAmount);
        cmp("Exc GST", report.total_exc_gst, totalExcGst);
        cmp("Inc GST", report.total_inc_gst, totalIncGst);
        cmp("Submitted", report.submission_date, submissionDate);
        cmp("Due Date", report.payment_due_date, paymentDueDate);
        cmp("Paid by Builder", report.paid_by_builder, paidByBuilder);
        cmp("Date Paid", report.date_paid, datePaid);
        cmp("Notes", report.notes, notes);
        return changes.length > 0 ? changes.join("; ") : "No changes";
    };

    const hasChanges = useMemo(() => {
        return buildChangesSummary() !== "No changes";
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        claimedToDate, variationsThisMonth, variationsOutstanding, claimThisMonth, retentionPct,
        retentionAmount, totalExcGst, totalIncGst, submissionDate,
        paymentDueDate, paidByBuilder, datePaid, notes,
    ]);

    // ─── Fetch version history ─────────────────────────────────
    const fetchVersions = async () => {
        setLoadingVersions(true);
        try {
            const { data, error } = await supabase
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                .from("claims_report_versions" as any)
                .select("*")
                .eq("report_id", report.id)
                .order("version_number", { ascending: false });
            if (error) throw error;
            setVersions((data as unknown as VersionRow[]) || []);
        } catch (err) {
            logError("Error loading versions:", err);
        } finally {
            setLoadingVersions(false);
        }
    };

    useEffect(() => {
        if (showHistory) fetchVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showHistory]);

    // ─── Save with version snapshot ────────────────────────────
    const handleSave = async () => {
        if (!hasChanges) {
            addToast({ type: "info", title: "No changes to save" });
            return;
        }

        setSaving(true);
        try {
            // 1. Get current user
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error("Not authenticated");

            // Get user's display name
            const { data: profile } = await supabase
                .from("profiles")
                .select("full_name")
                .eq("id", user.id)
                .single();

            // 2. Get current version count
            const { count } = await supabase
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                .from("claims_report_versions" as any)
                .select("*", { count: "exact", head: true })
                .eq("report_id", report.id);

            const nextVersion = (count || 0) + 1;

            // 3. Create version snapshot (BEFORE updating)
            const snapshot: Record<string, unknown> = {};
            const snapshotFields = [
                "claimed_to_date", "variations_this_month", "variations_outstanding", "subtotal",
                "claim_this_month", "retention_pct", "retention_amount", "retention_release_amount",
                "retention_release_gst", "retention_release_total_inc_gst", "total_exc_gst", "total_inc_gst",
                "calculated_payment_amount", "submission_date", "payment_due_date", "paid_by_builder", "date_paid", "notes", "pdf_url",
                "progress_payment_pct", "variation_payments",
            ] as const;
            for (const key of snapshotFields) {
                snapshot[key] = report[key as keyof ClaimReport];
            }

            const { error: versionError } = await supabase
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                .from("claims_report_versions" as any)
                .insert({
                    organization_id: requireOrganizationId(),
                    report_id: report.id,
                    version_number: nextVersion,
                    version_type: "edit",
                    pdf_url: report.pdf_url || null,
                    snapshot,
                    changes_summary: buildChangesSummary(),
                    changed_by: user.id,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                    changed_by_name: (profile as any)?.full_name || user.email || "Unknown",
                });

            if (versionError) throw versionError;

            // 4. Calculate difference and days_before_due
            const calculatedPaymentAmount = Number(totalIncGst || 0);
            const paidAmt = Number(paidByBuilder || 0);
            const diff = roundCurrency(calculatedPaymentAmount - paidAmt);

            let daysBeforeDue: number | null = null;
            if (paymentDueDate) {
                const due = new Date(paymentDueDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                due.setHours(0, 0, 0, 0);
                daysBeforeDue = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            }

            // 5. Update the report
            const { error: updateError } = await supabase
                .from("claims_reports")
                .update({
                    claimed_to_date: claimedToDate !== '' ? Number(claimedToDate) : null,
                    variations_this_month: variationsThisMonth !== '' ? Number(variationsThisMonth) : null,
                    variations_outstanding: variationsOutstanding !== '' ? Number(variationsOutstanding) : null,
                    subtotal: subtotal !== '' ? Number(subtotal) : null,
                    claim_this_month: claimThisMonth !== '' ? Number(claimThisMonth) : null,
                    retention_pct: retentionPct !== '' ? Number(retentionPct) : null,
                    retention_amount: retentionAmount !== '' ? Number(retentionAmount) : null,
                    total_exc_gst: totalExcGst !== '' ? Number(totalExcGst) : null,
                    total_inc_gst: totalIncGst !== '' ? Number(totalIncGst) : null,
                    calculated_payment_amount: totalIncGst !== '' ? calculatedPaymentAmount : null,
                    submission_date: submissionDate || null,
                    payment_due_date: paymentDueDate || null,
                    paid_by_builder: paidByBuilder !== '' ? Number(paidByBuilder) : null,
                    date_paid: datePaid || null,
                    notes: notes || null,
                    difference: diff || null,
                    days_before_due: daysBeforeDue,
                })
                .eq("id", report.id);

            if (updateError) throw updateError;

            // 6. Regenerate the PDF summary from the edited values and re-merge
            //    the existing attachment pages. Independent of the Send Report
            //    path; keeps appended documents (variations/statutory) intact.
            try {
                const regenInput: RegenReport = {
                    id: report.id,
                    project_id: report.project_id,
                    claim_number: report.claim_number,
                    claim_month: report.claim_month,
                    claim_year: report.claim_year,
                    variations_this_month: variationsThisMonth !== '' ? Number(variationsThisMonth) : 0,
                    variations_outstanding: variationsOutstanding !== '' ? Number(variationsOutstanding) : 0,
                    subtotal: subtotal !== '' ? Number(subtotal) : null,
                    claim_this_month: claimThisMonth !== '' ? Number(claimThisMonth) : null,
                    retention_amount: retentionAmount !== '' ? Number(retentionAmount) : 0,
                    retention_release_amount: report.retention_release_amount ?? 0,
                    retention_release_gst: report.retention_release_gst ?? null,
                    retention_release_total_inc_gst: report.retention_release_total_inc_gst ?? null,
                    total_exc_gst: totalExcGst !== '' ? Number(totalExcGst) : null,
                    total_inc_gst: totalIncGst !== '' ? Number(totalIncGst) : null,
                    claimed_set: report.claimed_set ?? null,
                    submission_date: submissionDate || report.submission_date,
                    payment_due_date: paymentDueDate || report.payment_due_date,
                    pdf_url: report.pdf_url,
                    pdf_filename: report.pdf_filename,
                };
                const regen = await regenerateClaimSummaryPdf(regenInput);
                if (regen) {
                    await supabase
                        .from("claims_reports")
                        .update({ pdf_url: regen.pdfUrl })
                        .eq("id", report.id);
                    addToast({
                        type: "success",
                        title: `Claim updated, PDF regenerated (${regen.pageCount} pages)`,
                    });
                } else {
                    addToast({ type: "success", title: "Claim updated, version saved" });
                }
            } catch (regenErr) {
                logError("PDF regeneration failed:", regenErr);
                addToast({
                    type: "info",
                    title: "Claim saved, but the PDF could not be regenerated",
                    message: regenErr instanceof Error ? regenErr.message : "Unknown error",
                });
            }
            onSaved();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : "Unknown error";
            logError("Save error:", err);
            addToast({ type: "error", title: `Save failed: ${message}` });
        } finally {
            setSaving(false);
        }
    };

    // ─── Revert to a version ───────────────────────────────────
    const handleRevert = async (version: VersionRow) => {
        setSaving(true);
        try {
            if (hasRestorablePaymentSnapshot(version as PaymentVersionRow)) {
                await revertToPaymentVersion(version as PaymentVersionRow);
                addToast({ type: "success", title: `Reverted to version ${version.version_number}` });
                setRevertConfirm(null);
                onSaved();
                return;
            }

            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error("Not authenticated");

            const { data: profile } = await supabase
                .from("profiles")
                .select("full_name")
                .eq("id", user.id)
                .single();

            // Create a revert version (snapshot of current state)
            const { count } = await supabase
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                .from("claims_report_versions" as any)
                .select("*", { count: "exact", head: true })
                .eq("report_id", report.id);

            const currentSnapshot: Record<string, unknown> = {};
            const snapshotFields = [
                "claimed_to_date", "variations_this_month", "variations_outstanding", "subtotal",
                "claim_this_month", "retention_pct", "retention_amount", "retention_release_amount",
                "retention_release_gst", "retention_release_total_inc_gst", "total_exc_gst", "total_inc_gst",
                "calculated_payment_amount", "submission_date", "payment_due_date", "paid_by_builder", "date_paid", "notes", "pdf_url",
                "progress_payment_pct", "variation_payments",
            ] as const;
            for (const key of snapshotFields) {
                currentSnapshot[key] = report[key as keyof ClaimReport];
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
            await supabase.from("claims_report_versions" as any).insert({
                organization_id: requireOrganizationId(),
                report_id: report.id,
                version_number: (count || 0) + 1,
                version_type: "revert",
                pdf_url: report.pdf_url || null,
                snapshot: currentSnapshot,
                changes_summary: `Reverted to version ${version.version_number}`,
                changed_by: user.id,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                changed_by_name: (profile as any)?.full_name || user.email || "Unknown",
            });

            // Apply the snapshot to the report
            const snap = version.snapshot;
            const calculatedPaymentAmount = Number(snap.calculated_payment_amount ?? snap.total_inc_gst ?? 0);
            const paidAmt = Number(snap.paid_by_builder || 0);

            let daysBeforeDue: number | null = null;
            if (snap.payment_due_date) {
                const due = new Date(snap.payment_due_date as string);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                due.setHours(0, 0, 0, 0);
                daysBeforeDue = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            }

            // Prefer the snapshot value when the key is PRESENT (even if null);
            // otherwise keep the current report value. This lets us revert a
            // 'payment' version (which only snapshots payment fields) without
            // nulling out the claim figures it doesn't carry.
            const snapRec = snap as Record<string, unknown>;
            const pick = <K extends keyof ClaimReport>(key: K): ClaimReport[K] =>
                (snapRec[key as string] !== undefined
                    ? snapRec[key as string]
                    : report[key]) as ClaimReport[K];

            const { error } = await supabase
                .from("claims_reports")
                .update({
                    claimed_to_date: pick("claimed_to_date") ?? null,
                    variations_this_month: pick("variations_this_month") ?? null,
                    variations_outstanding: pick("variations_outstanding") ?? null,
                    subtotal: pick("subtotal") ?? null,
                    claim_this_month: pick("claim_this_month") ?? null,
                    retention_pct: pick("retention_pct") ?? null,
                    retention_amount: pick("retention_amount") ?? null,
                    retention_release_amount: pick("retention_release_amount") ?? null,
                    retention_release_gst: pick("retention_release_gst") ?? null,
                    retention_release_total_inc_gst: pick("retention_release_total_inc_gst") ?? null,
                    total_exc_gst: pick("total_exc_gst") ?? null,
                    total_inc_gst: pick("total_inc_gst") ?? null,
                    calculated_payment_amount: calculatedPaymentAmount || null,
                    submission_date: pick("submission_date") ?? null,
                    payment_due_date: pick("payment_due_date") ?? null,
                    paid_by_builder: pick("paid_by_builder") ?? null,
                    date_paid: pick("date_paid") ?? null,
                    notes: pick("notes") ?? null,
                    pdf_url: version.pdf_url || (snap.pdf_url as string) || null,
                    difference: roundCurrency(calculatedPaymentAmount - paidAmt) || null,
                    days_before_due: daysBeforeDue,
                })
                .eq("id", report.id);

            if (error) throw error;

            addToast({ type: "success", title: `Reverted to version ${version.version_number}` });
            setRevertConfirm(null);
            onSaved();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : "Unknown error";
            logError("Revert error:", err);
            addToast({ type: "error", title: `Revert failed: ${message}` });
        } finally {
            setSaving(false);
        }
    };

    // ─── Render ─────────────────────────────────────────────────
    const period = `${MONTH_SHORT[report.claim_month - 1]} ${report.claim_year}`;

    const fieldRow = (
        label: string,
        value: string,
        setter: (v: string) => void,
        type: "number" | "date" | "text" = "number",
        locked?: boolean,
        lockToggle?: () => void,
        lockLabel?: string,
    ) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <label
                    className="form-label"
                    style={{ margin: 0, fontSize: "0.72rem", fontWeight: 600 }}
                >
                    {label}
                </label>
                {lockToggle && (
                    <button
                        onClick={lockToggle}
                        title={locked ? `Unlock ${lockLabel || label} for manual override` : `Lock ${lockLabel || label} to auto-calculate`}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "2px",
                            display: "flex",
                            alignItems: "center",
                            color: locked ? "var(--text-muted)" : "#f59e0b",
                            transition: "color 0.15s",
                        }}
                    >
                        {locked ? <Lock size={11} /> : <Unlock size={11} />}
                    </button>
                )}
            </div>
            <input
                type={type}
                data-seq-private={type === "date" ? undefined : ""}
                className="form-input"
                value={value}
                onChange={(e) => setter(e.target.value)}
                disabled={locked}
                style={{
                    fontSize: "0.82rem",
                    padding: "6px 10px",
                    opacity: locked ? 0.65 : 1,
                    background: locked ? "var(--bg-tertiary)" : undefined,
                }}
                step={type === "number" ? "0.01" : undefined}
            />
        </div>
    );

    const fmtVersionDate = (iso: string) => {
        const d = new Date(iso);
        return d.toLocaleDateString("en-AU", {
            day: "numeric", month: "short", year: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal"
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: "620px", maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column" }}
            >
                {/* Header */}
                <div className="modal-header" style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-color)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div
                            style={{
                                width: "32px", height: "32px", borderRadius: "8px",
                                background: "rgba(59,130,246,0.1)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                        >
                            <FileText size={16} color="#3b82f6" />
                        </div>
                        <div>
                            <h3 className="modal-title" style={{ margin: 0, fontSize: "1rem" }}>
                                Edit Claim #{report.claim_number}
                            </h3>
                            <p style={{ margin: 0, fontSize: "0.72rem", color: "var(--text-muted)" }}>
                                {period} • {report.is_manual_entry ? "Manual Entry" : "Generated"}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: "none", border: "none", cursor: "pointer",
                            color: "var(--text-muted)", padding: "4px",
                        }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Scrollable body */}
                <div style={{ overflow: "auto", flex: 1, padding: "16px 20px" }}>
                    {/* PDF regeneration notice */}
                    {report.pdf_url && (
                        <div
                            style={{
                                display: "flex", alignItems: "center", gap: "8px",
                                padding: "10px 12px", borderRadius: "8px",
                                background: "rgba(59,130,246,0.08)",
                                border: "1px solid rgba(59,130,246,0.2)",
                                marginBottom: "16px", fontSize: "0.75rem", color: "#2563eb",
                            }}
                        >
                            <FileText size={14} />
                            <span>Saving will <b>regenerate the invoice summary page</b> with these values. Existing attachments (variations, statutory declaration) are kept.</span>
                        </div>
                    )}

                    {/* Form Grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                        {fieldRow("Claimed to Date ($)", claimedToDate, setClaimedToDate)}
                        {fieldRow("Variations This Month ($)", variationsThisMonth, setVariationsThisMonth)}
                        {fieldRow("Variations Outstanding ($)", variationsOutstanding, setVariationsOutstanding)}
                        {fieldRow("Claim This Month (Ex GST)", claimThisMonth, setClaimThisMonthManual, "number", lockThisClaim, () => setLockThisClaim(!lockThisClaim))}
                        {fieldRow("Retention %", retentionPct, setRetentionPct)}
                        {fieldRow("Retention Held ($)", retentionAmount, setRetentionAmount)}
                        {fieldRow("Total Ex GST ($)", totalExcGst, setTotalExcGst, "number", lockExcGst, () => setLockExcGst(!lockExcGst))}
                        {fieldRow("Total Payable (Inc GST)", totalIncGst, setTotalIncGst, "number", lockIncGst, () => setLockIncGst(!lockIncGst))}
                        {fieldRow("Submitted", submissionDate, setSubmissionDate, "date")}
                        {fieldRow("Due Date", paymentDueDate, setPaymentDueDate, "date")}
                        {fieldRow("Paid by Builder ($)", paidByBuilder, setPaidByBuilder)}
                        {fieldRow("Date Paid", datePaid, setDatePaid, "date")}
                    </div>

                    {/* Notes */}
                    <div style={{ marginTop: "12px" }}>
                        <label className="form-label" style={{ fontSize: "0.72rem", fontWeight: 600 }}>Notes</label>
                        <textarea
                            className="form-input"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            style={{ fontSize: "0.82rem", padding: "6px 10px", resize: "vertical" }}
                            placeholder="Optional notes..."
                        />
                    </div>

                    {/* Version History Toggle */}
                    <button
                        onClick={() => setShowHistory(!showHistory)}
                        style={{
                            marginTop: "16px", width: "100%",
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "10px 12px", borderRadius: "8px",
                            background: "var(--bg-secondary)", border: "1px solid var(--border-color)",
                            cursor: "pointer", fontSize: "0.78rem", fontWeight: 600,
                            color: "var(--text-primary)",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <History size={14} color="var(--text-muted)" />
                            Version History
                            {versions.length > 0 && (
                                <span style={{
                                    background: "rgba(59,130,246,0.1)", color: "#3b82f6",
                                    padding: "1px 6px", borderRadius: "10px", fontSize: "0.68rem", fontWeight: 700,
                                }}>
                                    {versions.length}
                                </span>
                            )}
                        </div>
                        {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>

                    {/* Version History List */}
                    {showHistory && (
                        <div
                            style={{
                                marginTop: "8px", border: "1px solid var(--border-color)",
                                borderRadius: "8px", overflow: "hidden", maxHeight: "240px", overflowY: "auto",
                            }}
                        >
                            {loadingVersions ? (
                                <div style={{ padding: "20px", textAlign: "center", color: "var(--text-muted)" }}>
                                    <Loader2 className="spinning" size={16} />
                                </div>
                            ) : versions.length === 0 ? (
                                <div style={{ padding: "16px", textAlign: "center", fontSize: "0.75rem", color: "var(--text-muted)" }}>
                                    No previous versions. This will be the first edit.
                                </div>
                            ) : (
                                versions.map((v) => (
                                    <div
                                        key={v.id}
                                        style={{
                                            padding: "10px 12px",
                                            borderBottom: "1px solid var(--border-color-light)",
                                            fontSize: "0.75rem",
                                        }}
                                    >
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                                <span
                                                    style={{
                                                        background: v.version_type === 'generation'
                                                            ? "rgba(168,85,247,0.1)" : "rgba(59,130,246,0.1)",
                                                        color: v.version_type === 'generation' ? "#a855f7" : "#3b82f6",
                                                        padding: "2px 6px", borderRadius: "4px",
                                                        fontWeight: 700, fontSize: "0.68rem",
                                                    }}
                                                >
                                                    V{v.version_number}
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: "0.58rem", fontWeight: 600,
                                                        background: v.version_type === 'generation'
                                                            ? "rgba(168,85,247,0.08)" : v.version_type === 'revert'
                                                            ? "rgba(245,158,11,0.08)" : v.version_type === 'payment'
                                                            ? "rgba(16,185,129,0.08)" : "rgba(59,130,246,0.08)",
                                                        color: v.version_type === 'generation'
                                                            ? "#a855f7" : v.version_type === 'revert'
                                                            ? "#d97706" : v.version_type === 'payment'
                                                            ? "#059669" : "#3b82f6",
                                                        padding: "1px 5px", borderRadius: "3px",
                                                        textTransform: "uppercase",
                                                    }}
                                                >
                                                    {v.version_type === 'generation' ? 'regenerated' : v.version_type === 'revert' ? 'reverted' : v.version_type === 'payment' ? 'payment' : 'edited'}
                                                </span>
                                                <span style={{ display: "flex", alignItems: "center", gap: "3px", color: "var(--text-muted)" }}>
                                                    <User size={10} /> {v.changed_by_name || "Unknown"}
                                                </span>
                                                <span style={{ display: "flex", alignItems: "center", gap: "3px", color: "var(--text-muted)" }}>
                                                    <Clock size={10} /> {fmtVersionDate(v.created_at)}
                                                </span>
                                                {v.pdf_url ? (
                                                    <a
                                                        href={v.pdf_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            fontSize: "0.6rem", fontWeight: 600,
                                                            color: "#3b82f6", textDecoration: "underline",
                                                            cursor: "pointer",
                                                        }}
                                                        title="View PDF from this version"
                                                    >
                                                        📄 PDF
                                                    </a>
                                                ) : null}
                                            </div>
                                            <button
                                                onClick={() => setRevertConfirm(v)}
                                                title={`Revert to version ${v.version_number}`}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: "3px",
                                                    background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)",
                                                    color: "#d97706", padding: "3px 8px", borderRadius: "4px",
                                                    cursor: "pointer", fontSize: "0.68rem", fontWeight: 600,
                                                }}
                                            >
                                                <RotateCcw size={10} /> Revert
                                            </button>
                                        </div>
                                        <p style={{ margin: "4px 0 0", color: "var(--text-secondary)", lineHeight: 1.4 }}>
                                            {v.changes_summary}
                                        </p>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="modal-footer"
                    style={{
                        padding: "12px 20px", borderTop: "1px solid var(--border-color)",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}
                >
                    <button className="btn btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                        style={{ minWidth: "140px" }}
                    >
                        {saving ? <Loader2 className="spinning" size={14} /> : <Save size={14} />}
                        <span>{saving ? "Saving..." : "Save Changes"}</span>
                    </button>
                </div>
            </div>

            {/* Revert Confirmation Modal */}
            {revertConfirm && (
                <div
                    className="modal-overlay"
                    onClick={() => setRevertConfirm(null)}
                    style={{ zIndex: 1001 }}
                >
                    <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "420px" }}>
                        <div className="modal-header">
                            <h3 className="modal-title">Revert to Version {revertConfirm.version_number}?</h3>
                        </div>
                        <div className="modal-body">
                            <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", lineHeight: 1.5 }}>
                                This will restore the claim data to the state <b>before</b> this edit was made. A new version will be created to record this revert, so you can always undo it.
                            </p>
                            <div
                                style={{
                                    marginTop: "12px", padding: "10px 12px", borderRadius: "8px",
                                    background: "var(--bg-secondary)", fontSize: "0.75rem",
                                    border: "1px solid var(--border-color)",
                                }}
                            >
                                <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                                    Changes in V{revertConfirm.version_number}:
                                </div>
                                <p style={{ margin: 0, color: "var(--text-secondary)" }}>{revertConfirm.changes_summary}</p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setRevertConfirm(null)}>
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => handleRevert(revertConfirm)}
                                disabled={saving}
                                style={{ background: "#d97706" }}
                            >
                                {saving ? <Loader2 className="spinning" size={14} /> : <RotateCcw size={14} />}
                                <span>{saving ? "Reverting..." : "Revert"}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
